import {
    onMounted,
    onUnmounted,
    ref,
    watch,
} from "vue";
import {
    useRoute,
    useRouter,
} from "vue-router";
import {
    PublicServerDirectoryResponseSchema,
    type PublicServerDirectoryResponse,
} from "@logscord/public-server";
import type { PublicServerDirectoryItem } from "./serverDirectoryShared.js";
import { useVuePublicRouteData } from "@logscord/public-site/adapters/vue";

const EmptyDirectory: PublicServerDirectoryResponse = {
    items      : [],
    pagination : {
        page       : 1,
        pageSize   : 100,
        totalItems : 0,
        totalPages : 0,
    },
    bounds: {
        membersMax : 100,
        onlineMax  : 0,
        voiceMax   : 0,
    },
};

const PublicDirectoryQueryKeys = [
    "q",
    "membersMin",
    "membersMax",
    "onlineMin",
    "onlineMax",
    "voiceMin",
    "voiceMax",
    "sort",
    "page",
] as const;

function readQueryValue(value: unknown): string {
    return typeof value === "string" ? value : "";
}

export function usePublicServerDirectory() {
    const route = useRoute();
    const router = useRouter();
    const publicRouteData = useVuePublicRouteData();
    const data = ref<PublicServerDirectoryResponse>(EmptyDirectory);
    const loading = ref(true);
    const failed = ref(false);
    const joinModalServer = ref<PublicServerDirectoryItem | null>(null);
    const search = ref("");
    const membersMin = ref("100");
    const membersMax = ref("");
    const onlineMin = ref("");
    const onlineMax = ref("");
    const voiceMin = ref("");
    const voiceMax = ref("");
    const sort = ref("members");
    const directoryPath = route.path;
    let stopRouteWatch: (() => void) | null = null;

    function synchronizeFromPublicRouteData(): boolean {
        const state = publicRouteData.state.value;
        loading.value = state?.status === "loading" || state?.status === "idle";
        const parsed = PublicServerDirectoryResponseSchema.safeParse(
            publicRouteData.getData("publicServers"),
        );

        if (parsed.success) {
            data.value = parsed.data;
            loading.value = state?.status === "loading";
            failed.value = false;

            if (!membersMax.value && parsed.data.bounds.membersMax > 100)
                membersMax.value = String(parsed.data.bounds.membersMax);

            return true;
        }

        if (state?.status === "unavailable" || state?.status === "not-found") {
            loading.value = false;
            failed.value = true;
        }

        return false;
    }

    function synchronizeFiltersFromRoute(): void {
        search.value = readQueryValue(route.query["q"]);
        membersMin.value = readQueryValue(route.query["membersMin"]) || "100";
        membersMax.value = readQueryValue(route.query["membersMax"]);
        onlineMin.value = readQueryValue(route.query["onlineMin"]);
        onlineMax.value = readQueryValue(route.query["onlineMax"]);
        voiceMin.value = readQueryValue(route.query["voiceMin"]);
        voiceMax.value = readQueryValue(route.query["voiceMax"]);
        sort.value = readQueryValue(route.query["sort"]) || "members";
    }

    function applyFilters(): void {
        const query: Record<string, string> = {};

        for (const [ key, value ] of [
            [ "q", search.value.trim() ],
            [ "membersMin", membersMin.value ],
            [ "membersMax", membersMax.value ],
            [ "onlineMin", onlineMin.value ],
            [ "onlineMax", onlineMax.value ],
            [ "voiceMin", voiceMin.value ],
            [ "voiceMax", voiceMax.value ],
            [ "sort", sort.value ],
        ]) {
            if (value)
                query[key] = value;
        }

        void router.push({
            path: route.path,
            query,
        });
    }

    function resetFilters(): void {
        search.value = "";
        membersMin.value = "100";
        membersMax.value = "";
        onlineMin.value = "";
        onlineMax.value = "";
        voiceMin.value = "";
        voiceMax.value = "";
        sort.value = "members";

        void router.push({
            path  : route.path,
            query : {},
        });
    }

    function goToPage(page: number): void {
        void router.push({
            path  : route.path,
            query : {
                ...route.query,
                page: String(page),
            },
        });
    }

    synchronizeFiltersFromRoute();

    const stopPublicRouteDataWatch = watch(
        publicRouteData.state,
        synchronizeFromPublicRouteData,
        { immediate: true },
    );

    onMounted(() => {
        stopRouteWatch = router.afterEach((to, from) => {
            if (to.path === directoryPath && to.fullPath !== from.fullPath) {
                synchronizeFiltersFromRoute();
            }
        });
        synchronizeFromPublicRouteData();
    });

    onUnmounted(() => {
        stopRouteWatch?.();
        stopPublicRouteDataWatch();
    });

    return {
        data,
        loading,
        failed,
        joinModalServer,
        search,
        membersMin,
        membersMax,
        onlineMin,
        onlineMax,
        voiceMin,
        voiceMax,
        sort,
        applyFilters,
        resetFilters,
        goToPage,
    };
}
